// How to install, for each platform (INSTALL.md §2, §4.3). Steps, not
// advice: the tools-not-instructions rule is about what to play, and these
// are a browser's own menus.
//
// THE IPHONE'S STEPS ARE iOS 26'S, checked 2026-10-02 against Apple's and
// MacRumors' instructions: Share is under ⋯ beside the address bar, Add to
// Home Screen can be behind View More, and the dialog has an Open as Web App
// switch that is on by default. Older iPhones have Share in the toolbar, and
// the first step says so rather than guessing which one this is.
//
// ON AN IPHONE OR IPAD THE APP KEEPS ITS OWN SIGN-IN (§1), whatever the
// browser: `signsInAgain` puts that sentence before the steps, at the moment
// of the decision (CLAUDE.md, "Say what will happen").

import type { IconName } from '../kit/Icon';
import type { InstallPlatform } from './platform';

export interface InstallStep {
  text: string;
  /** What to look for on screen, when the step names a button. */
  glyph?: IconName;
}

export interface InstallGuide {
  platform: InstallPlatform;
  /** "iPhone or iPad — Safari": what the other-device list calls it. */
  label: string;
  steps: InstallStep[];
  /** Said after the steps. */
  note?: string;
  /** The installed app starts signed out (§1): said before the steps. */
  signsInAgain: boolean;
  /** This browser can raise its own install dialog from our button (§2). */
  canPrompt: boolean;
}

export const SIGN_IN_AGAIN =
  'On an iPhone or iPad the app keeps its own sign-in, apart from your browser, so you will sign in once more the first time you open it. It happens once.';

export const GUIDES: readonly InstallGuide[] = [
  {
    platform: 'ios-safari',
    label: 'iPhone or iPad — Safari',
    signsInAgain: true,
    canPrompt: false,
    steps: [
      {
        text: 'Tap ⋯ beside the address bar, then Share. On older iPhones, Share is in the toolbar at the bottom.',
        glyph: 'share-sheet',
      },
      {
        text: "Tap Add to Home Screen. If it isn't in the list, tap View More first.",
        glyph: 'add-to-home',
      },
      { text: 'Leave Open as Web App on, and tap Add.' },
      { text: 'Open it from your Home Screen and sign in.' },
    ],
  },
  {
    platform: 'ios-other',
    label: 'iPhone or iPad — Chrome, Edge or Firefox',
    signsInAgain: true,
    canPrompt: false,
    steps: [
      {
        text: "Tap the Share button. In Chrome it's at the right of the address bar.",
        glyph: 'share-sheet',
      },
      {
        text: "Tap Add to Home Screen. If it isn't there, tap View More or More first.",
        glyph: 'add-to-home',
      },
      { text: 'Tap Add, then open it from your Home Screen and sign in.' },
    ],
  },
  {
    platform: 'android-chrome',
    label: 'Android — Chrome',
    signsInAgain: false,
    canPrompt: true,
    steps: [
      { text: 'Tap ⋮ at the top right.', glyph: 'browser-menu' },
      { text: 'Tap Install app. If it says Add to Home screen, tap that, then Install.' },
      { text: 'Open it from your home screen or your apps.' },
    ],
  },
  {
    platform: 'android-other',
    label: 'Android — Samsung Internet, Firefox or another browser',
    signsInAgain: false,
    canPrompt: false,
    steps: [
      { text: "Open your browser's menu.", glyph: 'browser-menu' },
      {
        text: 'Tap Install or Add to Home screen. In Samsung Internet it is Add page to, then Home screen.',
        glyph: 'add-to-home',
      },
      { text: 'Open it from your home screen.' },
    ],
    note: 'Chrome installs it as a full app. Some browsers add a shortcut that opens in the browser instead.',
  },
  {
    platform: 'desktop-chrome',
    label: 'Computer — Chrome',
    signsInAgain: false,
    canPrompt: true,
    steps: [
      { text: 'Click the install icon at the right end of the address bar.', glyph: 'install' },
      { text: 'Or open ⋮, then Cast, save, and share, then Install.', glyph: 'browser-menu' },
      { text: 'It opens in its own window, and stays in your apps from then on.' },
    ],
  },
  {
    platform: 'desktop-edge',
    label: 'Computer — Edge',
    signsInAgain: false,
    canPrompt: true,
    steps: [
      { text: 'Click the install icon at the right end of the address bar.', glyph: 'install' },
      { text: 'Or open ⋯, then Apps, then Install this site as an app.', glyph: 'more' },
      { text: 'It opens in its own window, and stays in your apps from then on.' },
    ],
  },
  {
    platform: 'mac-safari',
    label: 'Mac — Safari',
    signsInAgain: false,
    canPrompt: false,
    steps: [
      { text: 'In the menu bar, choose File, then Add to Dock.' },
      { text: 'Click Add. It opens from the Dock, in its own window.' },
    ],
  },
  {
    platform: 'desktop-firefox',
    label: 'Computer — Firefox',
    signsInAgain: false,
    canPrompt: false,
    steps: [],
    note: "Firefox on a computer can't install a site as an app. Chrome, Edge and Safari can: open this site in one of them and install it from there.",
  },
  {
    platform: 'other',
    label: 'Another browser',
    signsInAgain: false,
    canPrompt: false,
    steps: [
      {
        text: "Look in your browser's menu for Install, or Add to Home Screen.",
        glyph: 'browser-menu',
      },
    ],
  },
];

export function guideFor(platform: InstallPlatform): InstallGuide {
  return GUIDES.find((g) => g.platform === platform) ?? GUIDES[GUIDES.length - 1]!;
}
